// Pestañas = TRABAJO REAL: solo objetos y procesos que el usuario decidió abrir (NV 10542, Cliente
// Botillería Central, Producto 2304, Despacho de una NV). Navegar por módulos, portadas y consultas NO
// crea pestañas: eso se ve en la vista de navegación, y `activa: null` significa «se está viendo la
// vista de navegación». Las pestañas se quedan MONTADAS mientras existan: cambiar de pestaña no pierde
// lo digitado. Abrir de nuevo el mismo objeto enfoca su pestaña (no la duplica). Todas pertenecen a la
// empresa activa: al cambiarla, se cierran.

export type TipoPestana =
  /** Nota de venta (existente o nueva) y el punto de venta (`params.modo = meson`). */
  | 'nota'
  /** Ficha Comercial 360 del cliente. */
  | 'contexto'
  | 'cliente'
  /** Despacho de una nota. */
  | 'despacho'
  /** Ficha del producto (presentaciones y stock). */
  | 'producto'
  /** Orden de Despacho (preparada: el dominio todavía no existe). */
  | 'od'
  | 'cobro'
  | 'pago'
  /** Documento de Compras (`params.doc`: oc, recepcion, factura, nota, devolucion). */
  | 'compra'
  /** Cotización (existente con `params.cotizacionId`, o nueva). */
  | 'cotizacion'
  /** Documento tributario emitido (`params.documentoId`): factura, boleta, NC… (2026-10-26). */
  | 'dte';

export interface Pestana {
  id: string;
  tipo: TipoPestana;
  titulo: string;
  /** Identidad para no abrir dos veces lo mismo (p. ej. `nota:<id>`). */
  clave: string;
  params: Record<string, string>;
  /** Tiene cambios sin guardar: cerrarla pide confirmación. */
  sucia: boolean;
}

export interface EstadoPestanas {
  pestanas: Pestana[];
  /** null = se ve la vista de navegación (ninguna pestaña al frente). */
  activa: string | null;
  secuencia: number;
}

export type AccionPestanas =
  | { tipo: 'abrir'; pestana: Omit<Pestana, 'id' | 'sucia'> }
  | { tipo: 'activar'; id: string }
  /** Volver a la vista de navegación (las pestañas siguen abiertas). */
  | { tipo: 'navegar' }
  | { tipo: 'cerrar'; id: string }
  | { tipo: 'titular'; id: string; titulo: string; clave?: string; params?: Record<string, string> }
  | { tipo: 'ensuciar'; id: string; sucia: boolean }
  | { tipo: 'reiniciar' };

export const estadoInicial: EstadoPestanas = { pestanas: [], activa: null, secuencia: 1 };

export function reducirPestanas(s: EstadoPestanas, a: AccionPestanas): EstadoPestanas {
  switch (a.tipo) {
    case 'abrir': {
      const existente = s.pestanas.find((p) => p.clave === a.pestana.clave);
      if (existente) return { ...s, activa: existente.id };
      const id = `t${s.secuencia}`;
      return {
        pestanas: [...s.pestanas, { ...a.pestana, id, sucia: false }],
        activa: id,
        secuencia: s.secuencia + 1,
      };
    }
    case 'activar':
      return s.pestanas.some((p) => p.id === a.id) ? { ...s, activa: a.id } : s;
    case 'navegar':
      return s.activa === null ? s : { ...s, activa: null };
    case 'cerrar': {
      const i = s.pestanas.findIndex((p) => p.id === a.id);
      if (i < 0) return s;
      const pestanas = s.pestanas.filter((p) => p.id !== a.id);
      // Cerrar la del frente deja al frente la vecina; si no queda ninguna, la vista de navegación.
      const activa =
        s.activa === a.id ? (pestanas[Math.min(i, pestanas.length - 1)]?.id ?? null) : s.activa;
      return { ...s, pestanas, activa };
    }
    case 'titular':
      return {
        ...s,
        pestanas: s.pestanas.map((p) =>
          p.id === a.id
            ? {
                ...p,
                titulo: a.titulo,
                ...(a.clave ? { clave: a.clave } : {}),
                ...(a.params ? { params: { ...p.params, ...a.params } } : {}),
              }
            : p,
        ),
      };
    case 'ensuciar':
      return {
        ...s,
        pestanas: s.pestanas.map((p) => (p.id === a.id ? { ...p, sucia: a.sucia } : p)),
      };
    case 'reiniciar':
      return { ...estadoInicial, secuencia: s.secuencia };
  }
}
